import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

function LogoCard({ client }) {
  return (
    <div className="clients-logo-card group relative flex-shrink-0">
      <div className="clients-logo-card-inner">
        <div className="clients-logo-glow" aria-hidden />
        <div className="clients-logo-frame">
          <img
            src={client.logo}
            alt={client.name}
            title={client.name}
            loading="lazy"
            decoding="async"
            draggable={false}
            className="clients-logo-img"
          />
        </div>
        <span className="clients-logo-label">{client.name}</span>
      </div>
    </div>
  );
}

export default function ClientsLogoCarousel({ clients, showCta = true }) {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const track = [...clients, ...clients];

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-b from-white via-slate-50/60 to-white dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-900"
      aria-label="Our clients"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#2563EB]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-[0.2em] uppercase rounded-full hero-glass-tag text-[#2563EB]">
            Trusted By
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-4">
            Our{' '}
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Clients
            </span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Organizations across aviation, education, healthcare, and government rely on Nisunajni for mission-critical technology.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="clients-carousel relative"
        >
          <div className="clients-carousel-fade clients-carousel-fade--left" aria-hidden />
          <div className="clients-carousel-fade clients-carousel-fade--right" aria-hidden />

          <div className="clients-carousel-track-wrapper">
            <div className="clients-carousel-track">
              {track.map((client, index) => (
                <LogoCard key={`${client.name}-${index}`} client={client} />
              ))}
            </div>
          </div>

          <div className="clients-carousel-track-wrapper clients-carousel-track-wrapper--reverse mt-5 md:mt-6 hidden sm:block">
            <div className="clients-carousel-track clients-carousel-track--reverse">
              {[...track].reverse().map((client, index) => (
                <LogoCard key={`rev-${client.name}-${index}`} client={client} />
              ))}
            </div>
          </div>
        </motion.div>

        {showCta && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="text-center mt-14 md:mt-16"
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border-2 border-[#2563EB]/25 text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-all duration-300 hero-glass-tag hover:shadow-lg hover:shadow-blue-500/15"
            >
              View Case Studies
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}
